import { useState, useEffect, useCallback, useRef } from "react";
import { BranchService } from "@/services/Branch/BranchSevice";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { branch } from "@/interface/branch.interface";
import type { IUser } from "@/interface/employeeOnBoard";
import type { IPageInfo } from "@/interface/common.interface";
import type { IBranchWiseSalaryHead } from "@/interface/salaryHead.interface";
import type { ISalaryUserBackup } from "@/components/common/SalaryConfigTable";
import type { IFilter } from "@/components/common/CommonFilter";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";

/**
 * Custom Hook for Branch Salary Backups & Configuration Management
 * Encapsulates all state management, API data fetching, file import/export,
 * and user salary updates while keeping the UI component ultra-clean.
 */
export const useBranchSalaryConfig = () => {
  // ── View Mode State ──────────────────────────────────────
  const [viewMode, setViewMode] = useState<"salary_backups" | "all_employees">(
    "salary_backups",
  );

  // ── Branch & Salary Head States ──────────────────────────
  const [branches, setBranches] = useState<branch[]>([]);
  const [branchesWiseSalaryHead, setBranchesWiseSalaryHead] = useState<
    IBranchWiseSalaryHead[]
  >([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [isLoadingBranches, setIsLoadingBranches] = useState<boolean>(false);

  // ── Employee List & Pagination State ─────────────────────
  const [employees, setEmployees] = useState<IUser[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
  const [filter, setFilter] = useState<IFilter>({ search: "" });
  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // ── Salary Backups Table & Pagination State ──────────────
  const [backupUsers, setBackupUsers] = useState<ISalaryUserBackup[]>([]);
  const [isLoadingBackups, setIsLoadingBackups] = useState<boolean>(false);
  const [backupPagination, setBackupPagination] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // ── Selection State ──────────────────────────────────────
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<
    Set<string | number>
  >(new Set());
  const [isAllBranchUsersSelected, setIsAllBranchUsersSelected] =
    useState<boolean>(false);

  // ── File Upload / Download Flow State ────────────────────
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 1. Fetch Branches ─────────────────────────────────────
  const handleFetchBranches = useCallback(async () => {
    setIsLoadingBranches(true);
    try {
      const response = await BranchService.getBranch("pageSize=100");
      if (response?.status && response?.data?.branch) {
        const branchList = response.data.branch;
        setBranches(branchList);
        setSelectedBranchId((prev) => (prev ? prev : branchList[0]?.id || ""));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranches(false);
    }
  }, []);

  // ── 2. Fetch Branch-Wise Salary Heads ────────────────────
  const handleFetchBranchesWiseSalaryHead = useCallback(
    async (branchId: string) => {
      if (!branchId) {
        setBranchesWiseSalaryHead([]);
        return;
      }
      try {
        const PATH = `branchId=${branchId}&pageSize=100`;
        const response = await SalaryHeadServices.getBranchWiseSalaryHead(PATH);
        if (response?.status || response?.statuscode === 200) {
          const salaryHeadData = response?.data?.branchSalaryHead || [];
          setBranchesWiseSalaryHead(salaryHeadData);
        } else {
          setBranchesWiseSalaryHead([]);
        }
      } catch (error) {
        ThrowError(error);
      }
    },
    [],
  );

  // ── 3. Fetch Regular Employees ───────────────────────────
  const handleFetchEmployees = useCallback(
    async (
      branchId: string,
      currentPage = paginationData.currentPage,
      currentLimit = paginationData.pageSize,
    ) => {
      if (!branchId) {
        setEmployees([]);
        return;
      }
      setIsLoadingEmployees(true);
      try {
        const paramsURL = GetFilterUrl({ ...filter });
        const PATH = `branchId=${branchId}&pageNumber=${currentPage}&pageSize=${currentLimit}&status=current${paramsURL ? `&${paramsURL}` : ""}`;
        const response = await EmployeeOnBoardService.getAllEmployees(PATH);
        if (response?.status) {
          const fetchedUsers = response?.data?.user || [];
          setEmployees(fetchedUsers);
          setPaginationData((prev) => ({
            ...prev,
            total: response?.data?.pageInfo?.total || 0,
            totalPages: response?.data?.pageInfo?.totalPages || 0,
          }));
        } else {
          setEmployees([]);
        }
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsLoadingEmployees(false);
      }
    },
    [filter, paginationData.currentPage, paginationData.pageSize],
  );

  // ── 4. Fetch Branch-Wise User Salary Backups ─────────────
  const handleFetchUserSalaryBackups = useCallback(
    async (
      branchId: string,
      currentPage = backupPagination.currentPage,
      currentLimit = backupPagination.pageSize,
    ) => {
      if (!branchId) {
        setBackupUsers([]);
        return;
      }
      setIsLoadingBackups(true);
      try {
        const searchParam = filter.search
          ? `&search=${encodeURIComponent(filter.search)}`
          : "";
        const PATH = `branchId=${branchId}&pageNumber=${currentPage}&pageSize=${currentLimit}${searchParam}`;
        const response = await SalaryHeadServices.getBranchWiseUserSalary(PATH);
        if (response?.status || response?.statuscode === 200) {
          const usersList: ISalaryUserBackup[] =
            response?.data?.users || response?.data?.user || [];
          setBackupUsers(usersList);
          setBackupPagination((prev) => ({
            ...prev,
            total: response?.data?.pageInfo?.total,
            totalPages: response?.data?.pageInfo?.totalPages,
          }));
        } else {
          setBackupUsers([]);
        }
      } catch (error) {
        ThrowError(error);
      } finally {
        setIsLoadingBackups(false);
      }
    },
    [filter.search, backupPagination.currentPage, backupPagination.pageSize],
  );

  // Initial branch fetch
  useEffect(() => {
    handleFetchBranches();
  }, [handleFetchBranches]);

  // Re-fetch data on branch, viewMode, pagination, or filter change
  useEffect(() => {
    if (selectedBranchId) {
      handleFetchBranchesWiseSalaryHead(selectedBranchId);
      if (viewMode === "salary_backups") {
        handleFetchUserSalaryBackups(
          selectedBranchId,
          backupPagination.currentPage,
          backupPagination.pageSize,
        );
      } else {
        handleFetchEmployees(
          selectedBranchId,
          paginationData.currentPage,
          paginationData.pageSize,
        );
      }
    }
  }, [
    selectedBranchId,
    viewMode,
    paginationData.currentPage,
    paginationData.pageSize,
    backupPagination.currentPage,
    backupPagination.pageSize,
    filter.search,
    handleFetchBranchesWiseSalaryHead,
    handleFetchEmployees,
    handleFetchUserSalaryBackups,
  ]);

  // Auto-check active users when "Select All Branch Users" is active
  const currentActiveUsers =
    viewMode === "salary_backups" ? backupUsers : employees;

  useEffect(() => {
    if (isAllBranchUsersSelected && currentActiveUsers.length > 0) {
      setSelectedEmployeeIds((prev) => {
        const next = new Set(prev);
        currentActiveUsers.forEach((emp) => {
          if (emp.id) next.add(String(emp.id));
        });
        return next;
      });
    }
  }, [currentActiveUsers, isAllBranchUsersSelected]);

  // ── Branch Switching ──────────────────────────────────────
  const handleBranchSelect = (branchId: string) => {
    if (branchId === selectedBranchId) return;
    setSelectedBranchId(branchId);
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    setBackupPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSelectedEmployeeIds(new Set());
    setIsAllBranchUsersSelected(false);
    setSelectedFile(null);
  };

  // ── Search & Pagination Handlers ─────────────────────────
  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    setBackupPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    if (viewMode === "salary_backups") {
      setBackupPagination((prev) => ({
        ...prev,
        pageSize: value,
        currentPage: 1,
      }));
    } else {
      setPaginationData((prev) => ({
        ...prev,
        pageSize: value,
        currentPage: 1,
      }));
    }
  };

  // ── Toggle "Select All Branch Users" ─────────────────────
  const handleToggleSelectAllBranchUsers = () => {
    setIsAllBranchUsersSelected((prev) => {
      const next = !prev;
      if (next) {
        const newSelected = new Set<string | number>();
        currentActiveUsers?.forEach((emp) => {
          if (emp.id) newSelected.add(String(emp.id));
        });
        setSelectedEmployeeIds(newSelected);
      } else {
        setSelectedEmployeeIds(new Set());
      }
      return next;
    });
  };

  // ── Selection Handlers ───────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Set(selectedEmployeeIds);
    if (checked) {
      currentActiveUsers.forEach((emp) => {
        if (emp.id) newSelected.add(String(emp.id));
      });
    } else {
      currentActiveUsers.forEach((emp) => {
        if (emp.id) newSelected.delete(String(emp.id));
      });
      setIsAllBranchUsersSelected(false);
    }
    setSelectedEmployeeIds(newSelected);
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!itemId) return;
    const newSelected = new Set(selectedEmployeeIds);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
      setIsAllBranchUsersSelected(false);
    }
    setSelectedEmployeeIds(newSelected);
  };

  const handleClearSelection = () => {
    setSelectedEmployeeIds(new Set());
    setIsAllBranchUsersSelected(false);
  };

  // ── Download Excel Template Handler ──────────────────────
  const canDownloadTemplate =
    (isAllBranchUsersSelected || selectedEmployeeIds.size > 0) &&
    Boolean(selectedBranchId);

  const handleDownloadTemplate = async () => {
    if (!canDownloadTemplate) {
      showToastNotification(
        "warning",
        "Please select branch users or click 'Select All Branch Users' first.",
      );
      return;
    }

    const payload = {
      branchId: selectedBranchId,
      userIds: isAllBranchUsersSelected
        ? []
        : Array.from(selectedEmployeeIds).map(String),
    };

    setIsDownloading(true);
    try {
      const blob = await SalaryHeadServices.BranchSalaryHeadTemplate(payload);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Salary_Head_Template_${selectedBranchId}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      showToastNotification("success", "Template downloaded successfully!");
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsDownloading(false);
    }
  };

  // ── File Selection & Excel Upload Handlers ───────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      showToastNotification("warning", "Please select a file to upload.");
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const params = selectedBranchId ? `branchId=${selectedBranchId}` : "";
      const response = await SalaryHeadServices.branchWiseSalaryImport(
        formData,
        params,
      );
      if (response?.status) {
        showToastNotification("success", "File uploaded successfully!");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        if (viewMode === "salary_backups") {
          handleFetchUserSalaryBackups(
            selectedBranchId,
            backupPagination.currentPage,
            backupPagination.pageSize,
          );
        } else {
          handleFetchEmployees(
            selectedBranchId,
            paginationData.currentPage,
            paginationData.pageSize,
          );
        }
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to parse import file",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsUploading(false);
    }
  };

  // ── Save Single User Salary Backup Handler ───────────────
  const handleSaveUserSalary = async (
    userId: string,
    userSalaryPayload: { branchSalaryHeadId: string; amount: number }[],
  ) => {
    const payload = {
      userSalary: userSalaryPayload,
    };
    try {
      const response = await SalaryHeadServices.updateUserSalary(
        payload,
        userId,
      );
      if (response?.status || response?.statuscode === 200) {
        showToastNotification(
          "success",
          "User salary backup updated successfully!",
        );
        handleFetchUserSalaryBackups(
          selectedBranchId,
          backupPagination.currentPage,
          backupPagination.pageSize,
        );
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to update user salary backup",
        );
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  const activeBranchName =
    branches.find((b) => b.id === selectedBranchId)?.name || "Branch";

  return {
    // State values
    viewMode,
    setViewMode,
    branches,
    branchesWiseSalaryHead,
    selectedBranchId,
    isLoadingBranches,
    employees,
    isLoadingEmployees,
    filter,
    paginationData,
    setPaginationData,
    backupUsers,
    isLoadingBackups,
    backupPagination,
    setBackupPagination,
    selectedEmployeeIds,
    isAllBranchUsersSelected,
    selectedFile,
    isDownloading,
    isUploading,
    fileInputRef,
    canDownloadTemplate,
    activeBranchName,

    // Handlers
    handleBranchSelect,
    handleSearchChange,
    handleChangePageSize,
    handleToggleSelectAllBranchUsers,
    handleSelectAll,
    handleSelectItem,
    handleClearSelection,
    handleDownloadTemplate,
    handleFileChange,
    handleClearFile,
    handleUploadFile,
    handleSaveUserSalary,
  };
};
